import React, { useState } from 'react';
import { BaseCRUDPage } from '../common';
import { Input, Textarea, RequiredIndicator, Column, FieldError } from '../../components';
import { shipperService } from '../../services';
import { Shipper, ShipperCreate, ShipperUpdate } from '../../types';

interface ShipperFormData {
  code: string;
  shortname: string;
  name: string;
  address: string;
  mobile: string;
  telephone: string;
}

const ShippersPage: React.FC = () => {
  const [formData, setFormData] = useState<ShipperFormData>({
    code: '',
    shortname: '',
    name: '',
    address: '',
    mobile: '',
    telephone: ''
  });

  // Define table columns
  const columns: Column[] = [
    { key: 'code', label: 'Code', sortable: true, searchable: true },
    { key: 'shortname', label: 'Short Name', sortable: true, searchable: true },
    { key: 'name', label: 'Name', sortable: true, searchable: true },
    { key: 'address', label: 'Address', sortable: true, searchable: true },
    { key: 'mobile', label: 'Mobile', sortable: true, searchable: true },
    { key: 'telephone', label: 'Telephone', sortable: true, searchable: true }
  ];

  // Form fields component with error support
  const formFields = (fieldErrors: Record<string, string>) => (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Shipper Code <RequiredIndicator />
        </label>
        <Input
          type="text"
          value={formData.code}
          onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
          required
          placeholder="e.g., SHP001"
        />
        <FieldError error={fieldErrors.code} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Short Name <RequiredIndicator />
        </label>
        <Input
          type="text"
          value={formData.shortname}
          onChange={(e) => setFormData(prev => ({ ...prev, shortname: e.target.value }))}
          required
          placeholder="e.g., ABC Shipping"
        />
        <FieldError error={fieldErrors.shortname} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Full Company Name <RequiredIndicator />
        </label>
        <Input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
          placeholder="e.g., ABC Shipping Corporation Ltd."
        />
        <FieldError error={fieldErrors.name} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Address
        </label>
        <Textarea
          value={formData.address}
          onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
          placeholder="Enter company address..."
          rows={2}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Mobile
        </label>
        <Input
          type="tel"
          value={formData.mobile}
          onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
          placeholder="e.g., +1-555-123-4567"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Telephone
        </label>
        <Input
          type="tel"
          value={formData.telephone}
          onChange={(e) => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
          placeholder="e.g., +1-555-987-6543"
        />
      </div>
    </div>
  );

  // Modal icon
  const modalIcon = (
    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
      </svg>
    </div>
  );

  return (
    <BaseCRUDPage<Shipper, ShipperCreate, ShipperUpdate>
      title="Shipper Management"
      entityName="shipper"
      columns={columns}
      service={shipperService}
      formFields={formFields}
      formData={formData}
      setFormData={setFormData}
      requiredFields={['code', 'shortname', 'name']}
      getInitialFormData={() => ({
        code: '',
        shortname: '',
        name: '',
        address: '',
        mobile: '',
        telephone: ''
      })}
      toCreateData={(formData) => ({
        code: formData.code,
        shortname: formData.shortname,
        name: formData.name,
        address: formData.address || undefined,
        mobile: formData.mobile || undefined,
        telephone: formData.telephone || undefined
      })}
      toUpdateData={(formData, original) => {
        const updateData: ShipperUpdate = {};
        if (formData.code !== original.code) updateData.code = formData.code;
        if (formData.shortname !== original.shortname) updateData.shortname = formData.shortname;
        if (formData.name !== original.name) updateData.name = formData.name;
        if (formData.address !== (original.address || '')) updateData.address = formData.address || null;
        if (formData.mobile !== (original.mobile || '')) updateData.mobile = formData.mobile || null;
        if (formData.telephone !== (original.telephone || '')) updateData.telephone = formData.telephone || null;
        return updateData;
      }}
      toFormData={(shipper) => ({
        code: shipper.code,
        shortname: shipper.shortname,
        name: shipper.name,
        address: shipper.address || '',
        mobile: shipper.mobile || '',
        telephone: shipper.telephone || ''
      })}
      modalIcon={modalIcon}
      modalTitle={(isEdit) => ({
        title: isEdit ? 'Edit Shipper' : 'Create New Shipper',
        subtitle: isEdit ? 'Update shipper information' : 'Add a new shipper to the system'
      })}
      customDeleteConfirm={(shipper) => `Are you sure you want to delete shipper "${shipper.name}"?`}
      defaultSearchColumn="code"
    />
  );
};

export default ShippersPage;
